import ky, { NormalizedOptions } from 'ky';

const baseUrl: string = process.env.REACT_APP_REST_API_URL as string;

export interface HttpResponse<T = unknown> {
  headers: Headers;
  status: number;
  body: T;
}

class HTTPService {
  private readonly instance: typeof ky;

  constructor() {
    this.instance = ky.extend({
      hooks: {
        beforeRequest: [this.logRequest],
        afterResponse: [this.logResponse],
      },
      prefixUrl: baseUrl,
      timeout: 30000,
      retry: 0,
    });
  }

  private logRequest = (request: Request): void => {
    console.log(
      'executing request: ',
      request.method,
      request.url,
      request.headers
    );
  };

  private logResponse = (
    request: Request,
    __: NormalizedOptions,
    response: Response
  ): Response => {
    console.log(
      'response acquired for url: ',
      request.url,
      response.status,
      response.headers
    );

    return response;
  };

  private getBody = async (response: Response): Promise<any> => {
    try {
      const isJson =
        response.headers.get('Content-Type') === 'application/json';
      const value = await (isJson ? response.json() : response.text());
      console.log('response body: ', value);
      return value;
    } catch (error) {
      return {};
    }
  };

  private request = async <T>(
    method: 'put' | 'post' | 'delete' | 'patch',
    url: string,
    data: Record<string, any>
  ): Promise<HttpResponse<T>> => {
    console.log('request body: ', data);
    const response = await this.instance(url, { json: data, method });

    return {
      body: await this.getBody(response),
      status: response.status,
      headers: response.headers,
    };
  };

  post = <T = unknown>(
    url: string,
    data: Record<string, any>
  ): Promise<HttpResponse<T>> => this.request('post', url, data);

  patch = <T = unknown>(
    url: string,
    data: Record<string, any>
  ): Promise<HttpResponse<T>> => this.request('patch', url, data);

  put = <T = unknown>(
    url: string,
    data: Record<string, any>
  ): Promise<HttpResponse<T>> => this.request('put', url, data);

  delete = <T = unknown>(
    url: string,
    data: Record<string, any>
  ): Promise<HttpResponse<T>> => this.request('delete', url, data);

  get = async <T = unknown>(
    url: string,
    search?: Record<string, string | number | boolean>
  ): Promise<HttpResponse<T>> => {
    const response = await this.instance.get(url, {
      searchParams: search,
    });
    return {
      body: await this.getBody(response),
      status: response.status,
      headers: response.headers,
    };
  };
}

const httpService = new HTTPService();
export default httpService;
